import { ReactNode, useState } from "react";
import { PaymentMethodButton } from ".";
import { Plaid } from "@/components/payment-methods";
import { Controller, useForm } from "react-hook-form";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Label } from "../ui/label";
import { Input } from "../ui/input";
import { Button } from "../ui/button-tw";
import { usePlatformData } from "@/hooks/platform/use-platform-data";
import { banks_logos } from "@/assets/images/banks";

type PlaidLinkCardProps = { description?: string; onSuccess?: () => void };

export function PlaidLinkCard({ description, onSuccess }: PlaidLinkCardProps) {
  const platformQuery = usePlatformData();
  const [initiateOpen, setInitiateOpen] = useState(false);
  const [initiateData, setInitiateData] = useState();
  const [plaidOpen, setPlaidOpen] = useState(false);

  const togglePlaidOpen = () => {
    setPlaidOpen((prev) => !prev);
  };

  return (
    <div className="flex overflow-hidden bg-white shadow-sm rounded-xl w-full max-w-[370px] h-72 border-[3px] border-border">
      <div className="flex flex-col justify-between p-6 md:p-8">
        <div>
          <div className={`max-w-10 h-auto`}>
            <img src={banks_logos["default"]} alt={`Bank Icon`} />
          </div>
          <h3 className="text-xl font-medium text-gray-900 flex-1 min-w-0 mt-6">
            Add a new bank account
          </h3>
          {description && <p>{description}</p>}
        </div>
        <div>
          <PaymentMethodButton
            handleConnect={() => setInitiateOpen(true)}
            //@ts-ignore
            primary_color={platformQuery.data?.primary_color}
          />
          <InitiateLinkAccount
            open={initiateOpen}
            onOpenChange={setInitiateOpen}
            onSubmit={(values) => {
              setInitiateData(values);
              setPlaidOpen(true);
            }}
          />
          {plaidOpen && initiateData ? (
            //@ts-ignore
            <Plaid
              open={plaidOpen}
              onClose={togglePlaidOpen}
              linkData={initiateData}
              method_type="PLAID"
              access_token=""
              onSuccess={onSuccess}
            />
          ) : null}
        </div>
      </div>
    </div>
  );
}

const InitiateSchema = z
  .object({
    accountType: z.enum([
      "Personal Checking",
      "Personal Savings",
      "Business Checking",
      "Business Savings",
    ]),
    firstName: z.string().min(1, "First name is required"),
    lastName: z.string().min(1, "Last name is required"),
    businessName: z.string().min(1, "Business name is required").optional(),
  })
  .superRefine((data, ctx) => {
    if (
      ["Business Checking", "Business Savings"].includes(data.accountType) &&
      (!data.businessName || data.businessName === "")
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Business name is required for business accounts",
        path: ["businessName"],
      });
    }
  });

export function InitiateLinkAccount({ open, onOpenChange, onSubmit }) {
  const {
    handleSubmit,
    register,
    formState: { errors, isValid },
    reset,
    watch,
    control,
  } = useForm({
    mode: "onChange",
    resolver: zodResolver(InitiateSchema),
  });

  const accountType = watch("accountType");
  const continueDisabled = !isValid;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* @ts-ignore */}
      <DialogContent className="sm:max-w-[425px]">
        <form
          onSubmit={handleSubmit(async (values) => {
            onSubmit(values);
            onOpenChange(false);
          })}
        >
          {/* @ts-ignore */}
          <DialogHeader>
            {/* @ts-ignore */}
            <DialogTitle className="text-xl">
              <div>Link a New Bank Account</div>
            </DialogTitle>
            {/* @ts-ignore */}
            <DialogDescription>
              To get started linking your bank account, we first need to confirm
              a couple quick details. After that, we’ll walk you through a
              secure step to complete the connection.
            </DialogDescription>
          </DialogHeader>
          {
            <div className="space-y-4 pt-4">
              <div className="w-full max-w-sm items-center gap-1.5">
                {/* @ts-ignore */}
                <Label htmlFor="accountType">Account Type</Label>
                <Controller
                  control={control}
                  name="accountType"
                  render={({ field, fieldState }) => {
                    return (
                      <Select
                        defaultValue={field.value}
                        onValueChange={field.onChange}
                        aria-invalid={fieldState.error ? "true" : "false"}
                      >
                        <SelectTrigger>
                          <SelectValue
                            placeholder="Select Account Type"
                            defaultValue={"Personal Checking"}
                          />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectGroup>
                            <SelectItem value="Personal Checking" className="">
                              Personal Checking
                            </SelectItem>
                            <SelectItem value="Personal Savings" className="">
                              Personal Savings
                            </SelectItem>
                            <SelectItem value="Business Checking">
                              Business Checking
                            </SelectItem>
                            <SelectItem value="Business Savings">
                              Business Savings
                            </SelectItem>
                          </SelectGroup>
                        </SelectContent>
                      </Select>
                    );
                  }}
                />
                {errors.accountType && (
                  <p role="alert" className="text-sm text-red-600">
                    {errors.accountType.message as ReactNode}
                  </p>
                )}
              </div>
              <div className="flex flex-col gap-y-4">
                {["Business Checking", "Business Savings"].includes(
                  accountType
                ) && (
                  <div>
                    {/* @ts-ignore */}
                    <Label htmlFor="businessName">Business Name</Label>
                    <Input
                      //@ts-ignore
                      placeholder="Business Name"
                      {...register("businessName")}
                    />
                  </div>
                )}
                {accountType && (
                  <div>
                    {/* @ts-ignore */}
                    <Label htmlFor="firstName">Business Representative</Label>
                    <div className="flex gap-x-3">
                      <Input
                        //@ts-ignore
                        placeholder="First Name"
                        {...register("firstName")}
                      />
                      <Input
                        //@ts-ignore
                        placeholder="Last Name"
                        {...register("lastName")}
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          }
          <DialogFooter className="mt-6">
            <Button
              variant="outline"
              className="w-1/2"
              type="button"
              onClick={() => {
                reset();
                onOpenChange(false);
              }}
            >
              Cancel
            </Button>
            <Button className="w-1/2" type="submit" disabled={continueDisabled}>
              Continue
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
